import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import { ArrowRight, Calendar, User, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      // Fallback to mock data if API fails
      setBlogPosts(mockData.blogPosts);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src={mockData.logo} alt="JurisGo Logo" className="h-8 w-8 object-contain" />
              <span className="text-2xl font-semibold text-slate-900">JurisGo</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Home</Link>
              <Link to="/trainings" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Trainings</Link>
              <Link to="/opportunities" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Opportunities</Link>
              <Link to="/blog" className="text-slate-900 font-semibold border-b-2 border-slate-900">Blog</Link>
              <Link to="/corporate" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Corporate</Link>
              <Link to="/wall-of-fame" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Wall of Fame</Link>
            </nav>
            <div className="flex items-center space-x-3">
              <Button asChild variant="outline" className="border-green-500 text-green-600 hover:bg-green-50 transition-colors">
                <a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span className="hidden lg:inline">Community</span>
                </a>
              </Button>
              <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white transition-colors">
                <a href="/#enroll">Enroll Now</a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-slate-700 text-white px-4 py-1">Insights & Resources</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            JurisGo Blog
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Expert insights, career tips, and industry knowledge for aspiring international paralegals
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading blogs...</p>
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No blog posts available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link to={`/blog/${post.id}`} key={post.id}>
                  <Card className="border-slate-200 hover:shadow-xl transition-all overflow-hidden group cursor-pointer h-full">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="secondary" className="bg-slate-100 text-slate-800">
                          {post.category}
                        </Badge>
                        <div className="flex items-center text-sm text-slate-500">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(post.created_at || post.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </div>
                      </div>
                      <CardTitle className="text-xl text-slate-900 group-hover:text-slate-700 transition-colors">
                        {post.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600 mb-4 line-clamp-3">{post.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-slate-500">
                          <User className="h-4 w-4 mr-1" />
                          {post.author}
                        </div>
                        <span className="text-slate-700 hover:text-slate-900 text-sm font-medium flex items-center">
                          Read More <ArrowRight className="ml-1 h-4 w-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* Coming Soon Message */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-slate-100 rounded-2xl px-8 py-6">
              <h3 className="text-2xl font-semibold text-slate-900 mb-2">More Articles Coming Soon</h3>
              <p className="text-slate-600">
                Subscribe to our newsletter to get notified about new blog posts and updates
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Paralegal Journey?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Join our training programs and become a certified international paralegal
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-8 py-6">
              <Link to="/trainings">View Programs <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="border-slate-400 text-white hover:bg-slate-800 text-lg px-8 py-6">
              <a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer">
                Join Community <Users className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-6 w-6 object-contain" />
                <span className="text-xl font-semibold text-white">JurisGo</span>
              </div>
              <p className="text-slate-400">India&apos;s Premier International Paralegal Training Platform</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
              <p className="mb-2">Email: {mockData.footer.email}</p>
              <p className="mb-2">Phone: {mockData.footer.phone}</p>
              <p>Location: {mockData.footer.address}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/trainings" className="hover:text-white transition-colors">Trainings</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/corporate" className="hover:text-white transition-colors">Corporate</Link></li>
                <li><a href="/#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><Link to="/admin" className="hover:text-white transition-colors">Admin</Link></li>
                <li><a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center">
                  <Users className="h-4 w-4 mr-1" /> Join Community
                </a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-8">
            <p className="text-sm text-slate-400 mb-4">{mockData.footer.disclaimer}</p>
            <p className="text-center text-slate-500">{mockData.footer.copyright}</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Community Button */}
      <div className="fixed bottom-6 right-6 z-[10000]">
        <Button 
          asChild 
          className="bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300"
          size="lg"
        >
          <a 
            href={mockData.whatsappGroup} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center space-x-2"
          >
            <Users className="h-6 w-6" />
            <span className="hidden sm:inline font-medium">Join Community</span>
          </a>
        </Button>
      </div>
    </div>
  );
};

export default Blog;
